// src/components/WorkoutStatsPanel.tsx
// Displays completed workout stats from Garmin with share functionality

import React, { useState, useEffect, Suspense, lazy } from 'react';
import { 
  Clock, 
  Zap, 
  Heart, 
  TrendingUp,
  Activity,
  Target,
  Flame,
  ChevronUp,
  ChevronDown,
  Share2,
  Loader2,
  RefreshCw,
  CheckCircle,
  Route,
  Timer,
  Gauge,
  BarChart3
} from 'lucide-react';
import { format } from 'date-fns';

// Lazy load WorkoutStoryCard to avoid html2canvas bundling issues
const WorkoutStoryCard = lazy(() => import('./WorkoutStoryCard'));

// ——————————————————————————————————
// TYPES
// ——————————————————————————————————
interface WorkoutActivity {
  id?: string;
  workout_id?: string;
  garmin_activity_id?: number;
  activity_name?: string;
  activity_type?: string;
  start_time?: string;
  distance_meters?: number;
  duration_seconds?: number;
  moving_duration_seconds?: number;
  calories?: number;
  average_hr?: number;
  max_hr?: number;
  average_speed?: number;
  max_speed?: number;
  average_power?: number;
  max_power?: number;
  normalized_power?: number;
  training_stress_score?: number;
  intensity_factor?: number;
  average_cadence?: number;
  max_cadence?: number;
  elevation_gain?: number;
  elevation_loss?: number;
  avg_stride_length?: number;
  hr_zones?: number[];
  power_zones?: number[];
  laps?: any[];
  splits?: any;
}

interface WorkoutStatsPanelProps {
  workoutId: string;
  workoutTitle?: string;
  workoutDate?: string;
  programName?: string;
  sport?: string;
  weekNumber?: number;
  dayNumber?: number;
  onFetchActivity?: () => Promise<WorkoutActivity | null>;
  initialActivity?: WorkoutActivity | null;
  isCompleted?: boolean; // Whether all sections are marked complete
}

// ——————————————————————————————————
// HELPER FUNCTIONS
// ——————————————————————————————————
function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatDistance(meters: number): string {
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} km`;
  }
  return `${Math.round(meters)} m`;
}

function formatPace(metersPerSecond: number, isSwim = false): string {
  if (!metersPerSecond || metersPerSecond === 0) return '--:--';
  
  if (isSwim) {
    const secsPer100m = 100 / metersPerSecond;
    const mins = Math.floor(secsPer100m / 60);
    const secs = Math.round(secsPer100m % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}/100m`;
  }
  
  const secsPerKm = 1000 / metersPerSecond;
  const mins = Math.floor(secsPerKm / 60);
  const secs = Math.round(secsPerKm % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}/km`;
}

function formatSpeed(metersPerSecond: number): string {
  const kmh = metersPerSecond * 3.6;
  return `${kmh.toFixed(1)} km/h`;
}

// ——————————————————————————————————
// STAT CARD COMPONENT (with empty state)
// ——————————————————————————————————
function StatCard({ 
  icon: Icon, 
  label, 
  value, 
  subValue,
  color = 'green',
  size = 'normal',
  isEmpty = false
}: { 
  icon: any; 
  label: string; 
  value?: string | number; 
  subValue?: string;
  color?: 'green' | 'blue' | 'yellow' | 'red' | 'purple' | 'orange';
  size?: 'normal' | 'large';
  isEmpty?: boolean;
}) {
  const colorClasses = {
    green: 'text-green-400 bg-green-500/10 border-green-500/20',
    blue: 'text-blue-400 bg-blue-500/10 border-blue-500/20',
    yellow: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20',
    red: 'text-red-400 bg-red-500/10 border-red-500/20',
    purple: 'text-purple-400 bg-purple-500/10 border-purple-500/20',
    orange: 'text-orange-400 bg-orange-500/10 border-orange-500/20',
  };

  const emptyClasses = 'text-gray-600 bg-gray-800/30 border-gray-700/30';

  return (
    <div className={`rounded-xl p-3 md:p-4 border transition-all ${isEmpty ? emptyClasses : colorClasses[color]} ${!isEmpty && 'hover:scale-[1.02]'}`}>
      <div className="flex items-start justify-between mb-2">
        <Icon className={`w-4 h-4 md:w-5 md:h-5 ${isEmpty ? 'text-gray-600' : colorClasses[color].split(' ')[0]}`} />
        {subValue && !isEmpty && (
          <span className="text-[10px] text-gray-500">{subValue}</span>
        )}
      </div>
      <p className={`font-bold ${size === 'large' ? 'text-2xl md:text-3xl' : 'text-lg md:text-xl'} ${isEmpty ? 'text-gray-600' : ''}`}>
        {isEmpty ? '—' : value}
      </p>
      <p className={`text-xs mt-1 ${isEmpty ? 'text-gray-600' : 'text-gray-500'}`}>{label}</p>
    </div>
  );
}

// ——————————————————————————————————
// HR ZONE BAR
// ——————————————————————————————————
function HRZoneBar({ zones }: { zones: number[] }) {
  const total = zones.reduce((a, b) => a + b, 0) || 1;
  const zoneColors = ['#22c55e', '#84cc16', '#eab308', '#f97316', '#ef4444'];
  const zoneNames = ['Recovery', 'Endurance', 'Tempo', 'Threshold', 'VO2 Max'];

  return (
    <div className="space-y-3">
      <div className="flex gap-0.5 h-6 rounded-lg overflow-hidden">
        {zones.map((time, idx) => (
          <div
            key={idx}
            className="transition-all duration-500 relative group"
            style={{
              width: `${(time / total) * 100}%`,
              backgroundColor: zoneColors[idx],
              minWidth: time > 0 ? '8px' : '0',
            }}
          >
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
              <div className="bg-gray-900 text-[#0f172a] text-xs rounded px-2 py-1 whitespace-nowrap">
                {zoneNames[idx]}: {formatDuration(time)}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-gray-500">
        {zoneNames.map((name, idx) => (
          <span key={idx} style={{ color: zoneColors[idx] }}>Z{idx + 1}</span>
        ))}
      </div>
    </div>
  );
}

// ——————————————————————————————————
// EMPTY HR ZONE BAR
// ——————————————————————————————————
function EmptyHRZoneBar() {
  return (
    <div className="space-y-3">
      <div className="flex gap-0.5 h-6 rounded-lg overflow-hidden bg-gray-800/50">
        {[1, 2, 3, 4, 5].map((_, idx) => (
          <div
            key={idx}
            className="flex-1 bg-gray-700/30"
          />
        ))}
      </div>
      <div className="flex justify-between text-[10px] text-gray-600">
        <span>Z1</span>
        <span>Z2</span>
        <span>Z3</span>
        <span>Z4</span>
        <span>Z5</span>
      </div>
    </div>
  );
}

// ——————————————————————————————————
// SPLITS TABLE
// ——————————————————————————————————
function SplitsTable({ laps, isSwim }: { laps: any[]; isSwim: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const displayLaps = expanded ? laps : laps.slice(0, 5);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-4 gap-2 text-xs text-gray-500 px-2">
        <span>Split</span>
        <span>Distance</span>
        <span>Time</span>
        <span>Pace</span>
      </div>
      {displayLaps.map((lap, idx) => (
        <div 
          key={idx} 
          className="grid grid-cols-4 gap-2 text-sm bg-gray-800/30 rounded-lg px-2 py-2 hover:bg-gray-800/50 transition"
        >
          <span className="font-medium">{idx + 1}</span>
          <span>{formatDistance(lap.distance)}</span>
          <span>{formatDuration(lap.duration)}</span>
          <span className="font-medium text-green-400">
            {lap.averageSpeed ? formatPace(lap.averageSpeed, isSwim) : '--'}
          </span>
        </div>
      ))}
      {laps.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full py-2 text-sm text-gray-500 hover:text-[#0f172a] transition flex items-center justify-center gap-1"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-4 h-4" />
              Show Less
            </>
          ) : (
            <>
              <ChevronDown className="w-4 h-4" />
              Show All {laps.length} Splits
            </>
          )}
        </button>
      )}
    </div>
  );
}

// ——————————————————————————————————
// MAIN COMPONENT
// ——————————————————————————————————
export default function WorkoutStatsPanel({
  workoutId,
  workoutTitle,
  workoutDate,
  programName,
  sport,
  weekNumber,
  dayNumber,
  onFetchActivity,
  initialActivity,
  isCompleted = false,
}: WorkoutStatsPanelProps) {
  const [activity, setActivity] = useState<WorkoutActivity | null>(initialActivity || null);
  const [loading, setLoading] = useState(false);
  const [showStoryCard, setShowStoryCard] = useState(false);

  // Update activity when initialActivity changes
  useEffect(() => {
    if (initialActivity) {
      setActivity(initialActivity);
    }
  }, [initialActivity]);

  const hasData = !!activity;
  const isRunning = (activity?.activity_type || sport || '').toLowerCase().includes('run');
  const isCycling = (activity?.activity_type || sport || '').toLowerCase().includes('cycl');
  const isSwimming = (activity?.activity_type || sport || '').toLowerCase().includes('swim');

  const fetchActivity = async () => {
    if (!onFetchActivity) return;
    
    setLoading(true);
    
    try {
      const data = await onFetchActivity();
      if (data) {
        setActivity(data);
      }
    } catch (err: any) {
      console.error('Failed to fetch activity:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-gradient-to-br from-gray-900/50 to-gray-800/30 rounded-2xl border border-gray-800/50 overflow-hidden">
        {/* Header */}
        <div className="p-4 md:p-6 border-b border-gray-800/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center ${
              hasData ? 'bg-green-500/20' : 'bg-gray-700/30'
            }`}>
              {hasData ? (
                <CheckCircle className="w-5 h-5 md:w-6 md:h-6 text-green-400" />
              ) : (
                <BarChart3 className="w-5 h-5 md:w-6 md:h-6 text-gray-500" />
              )}
            </div>
            <div>
              <h3 className="font-bold text-base md:text-lg">
                {hasData ? 'Workout Complete' : 'Workout Stats'}
              </h3>
              <p className="text-xs md:text-sm text-gray-500">
                {hasData && activity?.start_time 
                  ? format(new Date(activity.start_time), 'd MMM yyyy, HH:mm')
                  : hasData 
                    ? workoutDate 
                    : 'Complete on Garmin to see stats'
                }
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Refresh button - always available as fallback */}
            <button
              onClick={fetchActivity}
              disabled={loading}
              className="p-2 text-gray-500 hover:text-[#0f172a] transition rounded-lg hover:bg-gray-800/50"
              title="Refresh stats"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            
            {/* Share button - only when data exists */}
            {hasData && (
              <button
                onClick={() => setShowStoryCard(true)}
                className="px-3 md:px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-[#0f172a] font-bold rounded-xl transition hover:opacity-90 flex items-center gap-2 text-sm"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}
          </div>
        </div>

        {/* Stats Grid */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {/* Primary Stats - Always show structure */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <StatCard
              icon={Route}
              label="Distance"
              value={hasData && activity?.distance_meters ? formatDistance(activity.distance_meters) : undefined}
              color="green"
              size="large"
              isEmpty={!hasData || !activity?.distance_meters}
            />
            
            <StatCard
              icon={Timer}
              label="Duration"
              value={hasData && activity?.duration_seconds ? formatDuration(activity.duration_seconds) : undefined}
              subValue={hasData && activity?.moving_duration_seconds 
                ? `Moving: ${formatDuration(activity.moving_duration_seconds)}`
                : undefined
              }
              color="blue"
              size="large"
              isEmpty={!hasData || !activity?.duration_seconds}
            />

            <StatCard
              icon={Gauge}
              label={isCycling ? 'Avg Speed' : 'Avg Pace'}
              value={hasData && activity?.average_speed 
                ? (isCycling 
                    ? formatSpeed(activity.average_speed)
                    : formatPace(activity.average_speed, isSwimming))
                : undefined
              }
              subValue={hasData && activity?.max_speed 
                ? `Max: ${isCycling 
                    ? formatSpeed(activity.max_speed)
                    : formatPace(activity.max_speed, isSwimming)
                  }`
                : undefined
              }
              color="orange"
              size="large"
              isEmpty={!hasData || !activity?.average_speed}
            />
          </div>

          {/* Secondary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            <StatCard
              icon={Heart}
              label="Avg HR"
              value={hasData && activity?.average_hr ? `${activity.average_hr} bpm` : undefined}
              subValue={hasData && activity?.max_hr ? `Max: ${activity.max_hr}` : undefined}
              color="red"
              isEmpty={!hasData || !activity?.average_hr}
            />

            <StatCard
              icon={Zap}
              label="Avg Power"
              value={hasData && activity?.average_power ? `${activity.average_power}w` : undefined}
              subValue={hasData && activity?.normalized_power ? `NP: ${activity.normalized_power}w` : undefined}
              color="yellow"
              isEmpty={!hasData || !activity?.average_power}
            />

            <StatCard
              icon={Flame}
              label="Calories"
              value={hasData ? activity?.calories : undefined}
              color="orange"
              isEmpty={!hasData || !activity?.calories}
            />

            <StatCard
              icon={Activity}
              label={isRunning ? 'Cadence' : 'Avg RPM'}
              value={hasData && activity?.average_cadence 
                ? (isRunning ? `${activity.average_cadence} spm` : activity.average_cadence)
                : undefined
              }
              subValue={hasData && activity?.max_cadence ? `Max: ${activity.max_cadence}` : undefined}
              color="purple"
              isEmpty={!hasData || !activity?.average_cadence}
            />

            <StatCard
              icon={TrendingUp}
              label="Elevation Gain"
              value={hasData && activity?.elevation_gain ? `${activity.elevation_gain}m` : undefined}
              subValue={hasData && activity?.elevation_loss ? `Loss: ${activity.elevation_loss}m` : undefined}
              color="green"
              isEmpty={!hasData || !activity?.elevation_gain}
            />

            <StatCard
              icon={Target}
              label="TSS"
              value={hasData && activity?.training_stress_score ? Math.round(activity.training_stress_score) : undefined}
              subValue={hasData && activity?.intensity_factor ? `IF: ${activity.intensity_factor.toFixed(2)}` : undefined}
              color="blue"
              isEmpty={!hasData || !activity?.training_stress_score}
            />
          </div>

          {/* HR Zones */}
          <div className="bg-gray-800/30 rounded-xl p-4">
            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <Heart className={`w-4 h-4 ${hasData && activity?.hr_zones ? 'text-red-400' : 'text-gray-600'}`} />
              <span className={hasData && activity?.hr_zones ? '' : 'text-gray-600'}>Heart Rate Zones</span>
            </h4>
            {hasData && activity?.hr_zones && activity.hr_zones.length > 0 ? (
              <HRZoneBar zones={activity.hr_zones} />
            ) : (
              <EmptyHRZoneBar />
            )}
          </div>

          {/* Splits/Laps - Only show if data exists */}
          {hasData && activity?.laps && activity.laps.length > 0 && (
            <div className="bg-gray-800/30 rounded-xl p-4">
              <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-blue-400" />
                Splits ({activity.laps.length})
              </h4>
              <SplitsTable laps={activity.laps} isSwim={isSwimming} />
            </div>
          )}

          {/* Empty state message */}
          {!hasData && (
            <p className="text-center text-gray-500 text-sm py-2">
              {isCompleted 
                ? 'Syncing with Garmin... Stats will appear automatically.'
                : 'Complete this workout on your Garmin to see your stats here.'
              }
            </p>
          )}
        </div>
      </div>

      {/* Story Card Modal */}
      {showStoryCard && activity && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
            <Loader2 className="w-8 h-8 animate-spin text-[#0f172a]" />
          </div>
        }>
          <WorkoutStoryCard
            activity={activity}
            workoutTitle={workoutTitle}
            programName={programName}
            sport={sport}
            weekNumber={weekNumber}
            dayNumber={dayNumber}
            onClose={() => setShowStoryCard(false)}
          />
        </Suspense>
      )}
    </>
  );
}
